import React, { useState, type JSX, type MouseEvent } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import { getStandardDrawerFloatingSx, useStandardDrawerLayerContainer } from 'components/Drawers/StandardDrawer';
import ExclusionOptionContent from 'components/Transactions/ExclusionOptionContent';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  root: {
    alignSelf: 'flex-start',
  },
  triggerField: {
    minWidth: 140,
    minHeight: 48,
    margin: 0,
    '& .MuiFilledInput-root': {
      minHeight: 48,
      height: 48,
      maxHeight: 48,
      alignItems: 'center',
      paddingLeft: theme.spacing(2),
      borderRadius: theme.shape.borderRadius * 3,
      backgroundColor: theme.palette.background.default,
    },
    '&:not(.Mui-disabled) .MuiFilledInput-root': {
      cursor: 'pointer',
    },
    '& .MuiFilledInput-input': {
      ...theme.typography.bodyRegular,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      padding: 0,
      height: 24,
    },
    '& .MuiFilledInput-input::placeholder': {
      ...theme.typography.bodyRegular,
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  popoverPaper: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 3,
    width: 260,
    boxShadow: theme.shadows[7],
  },
  title: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  option: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr)',
    columnGap: theme.spacing(1),
    alignItems: 'start',
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1.5),
    },
  },
  checkbox: {
    marginTop: 2,
  },
  triggerIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  triggerIcon: {
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  triggerEndAdornment: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

type Props = {
  id?: string;
  className?: string;
  excludeReports?: boolean | null;
  excludeF2S?: boolean | null;
  setExcludeReports: (exclude: boolean) => void;
  setExcludeF2S: (exclude: boolean) => void;
  disabled?: boolean;
};

const ExclusionSettings = ({
  id = 'split-exclusions',
  className,
  excludeReports = false,
  excludeF2S = false,
  setExcludeReports,
  setExcludeF2S,
  disabled = false,
}: Props): JSX.Element => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasSelections = Boolean(excludeReports || excludeF2S);
  const popoverSx = getStandardDrawerFloatingSx();
  const drawerLayerContainer = useStandardDrawerLayerContainer();

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const options = [
    {
      id: 'split-exclusion-spending-plan',
      label: 'Spending Plan',
      helperText: 'Affects Spending Plan calculations.',
      checked: excludeF2S,
      onChange: setExcludeF2S,
      Icon: ExcludeFromSpendingPlan,
    },
    {
      id: 'split-exclusion-reports',
      label: 'Reports',
      helperText: 'Affects Reports, Watchlists, and Upcoming Summary.',
      checked: excludeReports,
      onChange: setExcludeReports,
      Icon: ExcludeFromReports,
    },
  ];

  return (
    <Box id={`${id}-root`} className={cx(classes.root, className)}>
      <TextField
        id={`${id}-trigger`}
        disabled={disabled}
        hiddenLabel
        margin='none'
        variant='filled'
        size='small'
        placeholder={hasSelections ? undefined : 'Exclusions'}
        value=''
        onClick={handleOpen}
        className={classes.triggerField}
        slotProps={{
          htmlInput: { readOnly: true },
          input: {
            startAdornment: hasSelections ? (
              <InputAdornment position='start'>
                <span className={classes.triggerIcons}>
                  {excludeF2S ? <ExcludeFromSpendingPlan className={classes.triggerIcon} /> : null}
                  {excludeReports ? <ExcludeFromReports className={classes.triggerIcon} /> : null}
                </span>
              </InputAdornment>
            ) : undefined,
            endAdornment: (
              <div className={classes.triggerEndAdornment}>
                <IconButton
                  tabIndex={-1}
                  disableRipple
                  size='small'
                  aria-hidden
                  sx={{ pointerEvents: 'none', p: 0.5, color: 'icon.main' }}
                >
                  <ArrowDropDownRoundedIcon fontSize='small' />
                </IconButton>
              </div>
            ),
          },
        }}
      />

      <Popover
        container={drawerLayerContainer ?? undefined}
        id={`${id}-popover`}
        open={anchorEl != null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={popoverSx}
        slotProps={{
          paper: {
            className: classes.popoverPaper,
            onClick: (event: MouseEvent<HTMLElement>) => event.stopPropagation(),
          },
        }}
      >
        <Typography variant='paragraphRegular' className={classes.title}>
          Exclude From:
        </Typography>
        {options.map((option) => (
          <div className={classes.option} key={option.id}>
            <Checkbox
              id={`${id}-${option.id}`}
              checked={option.checked ?? false}
              onChange={(_event, checked) => option.onChange(checked)}
              sx={{ p: 0 }}
              className={classes.checkbox}
            />
            <ExclusionOptionContent
              label={option.label}
              helperText={option.helperText}
              Icon={option.Icon}
              selected={Boolean(option.checked)}
            />
          </div>
        ))}
      </Popover>
    </Box>
  );
};

export default ExclusionSettings;
